'use client'

import { type ReactNode } from 'react'

import { AnchorTarget } from '@/components/common/AnchorTarget'
import { ButtonRoot } from '@/components/common/Button'
import { LinkRoute } from '@/components/common/Link'
import { LoadingMask } from '@/components/common/LoadingMask'
import { OptionCount } from '@/components/common/OptionCount'
import { ParsedTemplate } from '@/components/common/ParsedTemplate'
import { ScrollOverflow } from '@/components/common/ScrollOverflow'
import { useIsNavigating } from '@/components/providers/NavigationProvider'
import { usePage } from '@/components/providers/PageProvider'
import { getRouteConfig } from '@/defs/route'
import { SCROLL_NAVTABS } from '@/defs/scroll'
import { useLinkInternal } from '@/hooks/useLinkInternal'
import { cn } from '@/utils/commonUtil'
import { getRouteTab } from '@/utils/routeUtil'
import { scrollToAnchor } from '@/utils/scrollUtil'

import styles from './NavTabs.module.scss'

type VariantType = 'bottom'

type ManualTabType = {
  count?: number
  label: string
  value: string
}

type Props = {
  children: ReactNode
} & (
  | {
      onClick: (value: string) => void
      tab: string
      tabs: ManualTabType[]
    }
  | {
      onClick?: never
      tab?: never
      tabs?: never
    }
)

type NavTabsRootProps = { activeKey: string; children: ReactNode; variant?: VariantType }

const NavTabsRoot = ({ activeKey, children, variant }: NavTabsRootProps) => {
  const isNavigating = useIsNavigating()

  return (
    <section className={cn(styles.container, { styles, variant })}>
      {!variant && <AnchorTarget id='nav-tabs' />}
      <ScrollOverflow activeKey={activeKey} offset={48}>
        <ul className={styles.list}>{children}</ul>
      </ScrollOverflow>
      <LoadingMask loading={isNavigating} />
    </section>
  )
}

const listButtonProps = (isSelected: boolean) => ({
  className: cn(styles.listButton, { isSelected, styles }),
  'data-scroll-active': isSelected || undefined,
})

export const NavTabs = (props: Props) => {
  const { children } = props
  const { buildRelative } = useLinkInternal()
  const { page } = usePage()
  const route = page?.route

  if (props.tabs?.length) {
    const { onClick, tab: selected, tabs } = props

    const onClickTab = (value: string) => {
      onClick(value)
      requestAnimationFrame(() => scrollToAnchor(SCROLL_NAVTABS))
    }

    const renderNavTabs = (variant?: VariantType) => (
      <NavTabsRoot activeKey={selected} variant={variant}>
        {tabs.map(({ count, label, value }) => (
          <li key={value}>
            <ButtonRoot {...listButtonProps(selected === value)} onClick={() => onClickTab(value)}>
              <span className={styles.label}>{label}</span>
              {!!count && <OptionCount count={count} />}
            </ButtonRoot>
          </li>
        ))}
      </NavTabsRoot>
    )

    return (
      <>
        {renderNavTabs()}
        {children}
        {renderNavTabs('bottom')}
      </>
    )
  }

  const tabValues = page?.tabValues
  const tabs =
    route && tabValues
      ? getRouteConfig(route.section)
          .tabs?.filter((t) => tabValues.includes(t.value))
          .map((t) => ({
            ...t,
            href: buildRelative(t.route),
          }))
          .filter((t): t is typeof t & { href: string } => !!t.href)
      : undefined

  const selected = getRouteTab(route, route?.tab)

  if (!selected || !tabs) return children

  const renderNavTabs = (variant?: VariantType) => (
    <NavTabsRoot activeKey={selected.value} variant={variant}>
      {tabs.map(({ href, label, value }) => (
        <li key={value}>
          <LinkRoute {...listButtonProps(selected.value === value)} href={href} scroll={SCROLL_NAVTABS}>
            <span className={styles.label}>
              <ParsedTemplate>{label}</ParsedTemplate>
            </span>
          </LinkRoute>
        </li>
      ))}
    </NavTabsRoot>
  )

  return (
    <>
      {renderNavTabs()}
      {children}
      {renderNavTabs('bottom')}
    </>
  )
}
